import { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { processorsAPI } from "@/lib/api";

/* Struktur nach DSGVO Art. 13/14. Alle Studio-Angaben sind eingetragen; es
   gibt keine offenen Platzhalter mehr (Stand 23.08.2026). Der Empfänger-
   Abschnitt lädt die Dienstleister live aus dem im CRM gepflegten Register
   (Einstellungen → Datenschutz → Dienstleister & AVV) — wird dort ein Dienst
   ergänzt, erscheint er hier automatisch. */

export default function DatenschutzPage() {
  const navigate = useNavigate();
  const [processors, setProcessors] = useState([]);

  useEffect(() => {
    processorsAPI.listPublic()
      .then(res => setProcessors(res.data.processors || []))
      .catch(() => setProcessors([]));
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="max-w-3xl mx-auto px-6 py-16">
        <button onClick={() => navigate(-1)} data-testid="datenschutz-back-btn" className="flex items-center gap-2 text-sm text-[#4B5563] hover:text-[#0A0A0A] mb-8">
          <ArrowLeft size={16} /> Zurück
        </button>
        <h1 className="text-3xl font-bold text-[#0A0A0A] mb-8">Datenschutzerklärung</h1>
        <div className="prose prose-gray max-w-none space-y-6 text-[#4B5563] leading-relaxed">

          <h2 className="text-xl font-bold text-[#0A0A0A]">1. Verantwortlicher</h2>
          <p>
            Verantwortlicher im Sinne der DSGVO ist:<br />
            P1 Performance Center GmbH<br />
            Geschäftsführer: Phi Philip Nguyen<br />
            Georg-Stieß-Straße 2<br />
            75181 Pforzheim<br />
            E-Mail: info@p1-pforzheim.de<br />
            Telefon: 0176 86477728
          </p>

          <h2 className="text-xl font-bold text-[#0A0A0A]">2. Zwecke und Rechtsgrundlagen der Verarbeitung</h2>
          <p>Wir verarbeiten personenbezogene Daten zu folgenden Zwecken:</p>
          <ul className="list-disc ml-6 space-y-2">
            <li><strong>Mitglieder- und Vertragsverwaltung</strong> (Name, Kontaktdaten, Geburtsdatum, Vertrags- und Zahlungsdaten, SEPA-Mandat) — Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).</li>
            <li><strong>Interessenten-Anfragen</strong> über unser Kontaktformular (Name, E-Mail, Telefon, Interesse) — Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen).</li>
            <li><strong>Kursbuchungen und Check-ins</strong> (Buchungs- und Anwesenheitsdaten) — Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.</li>
            <li><strong>Trainingsdaten</strong> in der Mitglieder-App (Trainingseinheiten, Übungen, Gurtfortschritt) — Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO; die Nutzung des Trainingsplaners ist freiwillig.</li>
            <li><strong>Werbliche Kommunikation</strong> (E-Mail/SMS zu Angeboten und Neuigkeiten) — Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung). Die Einwilligung ist freiwillig und jederzeit mit Wirkung für die Zukunft widerrufbar.</li>
            <li><strong>Kommunikation per E-Mail und WhatsApp</strong> (Kontaktdaten, Inhalt der Nachrichten) — Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO für Buchungen und Vertragsfragen, Art. 6 Abs. 1 lit. a DSGVO für werbliche Nachrichten. Einzelheiten in Abschnitt 11.</li>
            <li><strong>Zahlungsabwicklung</strong> über unseren Zahlungsdienstleister — Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.</li>
            <li><strong>Gesetzliche Aufbewahrungspflichten</strong> (z. B. Rechnungsdaten) — Rechtsgrundlage: Art. 6 Abs. 1 lit. c DSGVO.</li>
          </ul>

          <h2 className="text-xl font-bold text-[#0A0A0A]">3. Empfänger und Auftragsverarbeiter</h2>
          <p>
            Wir setzen zur Bereitstellung unserer Leistungen folgende Dienstleister ein, mit denen — soweit
            erforderlich — Verträge zur Auftragsverarbeitung (Art. 28 DSGVO) bestehen:
          </p>
          {processors.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-[#0A0A0A]">Dienstleister</th>
                    <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-[#0A0A0A]">Zweck</th>
                    <th className="text-left px-3 py-2 border-b border-gray-200 font-semibold text-[#0A0A0A]">Standort</th>
                  </tr>
                </thead>
                <tbody data-testid="datenschutz-processors">
                  {processors.map(p => (
                    <tr key={p.id} className="border-b border-gray-100 last:border-0">
                      <td className="px-3 py-2">{p.name}</td>
                      <td className="px-3 py-2">{p.purpose}</td>
                      <td className="px-3 py-2">{p.location || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm italic">Die aktuelle Dienstleisterliste wird geladen bzw. ist derzeit nicht verfügbar.</p>
          )}
          <p className="text-sm">
            Bei Dienstleistern mit Sitz außerhalb der EU/des EWR erfolgt die Übermittlung auf Grundlage von
            EU-Standardvertragsklauseln (Art. 46 DSGVO) bzw. eines Angemessenheitsbeschlusses.
          </p>

          <h2 className="text-xl font-bold text-[#0A0A0A]">4. Speicherdauer</h2>
          <p>
            Wir speichern personenbezogene Daten nur so lange, wie es für die genannten Zwecke erforderlich ist
            oder gesetzliche Aufbewahrungspflichten bestehen (z. B. 10 Jahre für Rechnungsunterlagen nach § 147 AO).
            Interessenten-Daten ohne Vertragsabschluss werden regelmäßig überprüft und nach spätestens 24 Monaten
            Inaktivität zur Löschung vorgesehen. Nach Vertragsende werden Mitgliedsdaten gelöscht, sobald keine
            Aufbewahrungspflichten mehr entgegenstehen.
          </p>

          {/* Pflichtangaben nach Art. 13 Abs. 2 lit. e und f DSGVO. Sie fehlten
              bisher — beide sind zwingend, auch wenn die Antwort "findet nicht
              statt" lautet.

              ACHTUNG bei Änderungen: Seit 12.10.2026 nennt Abschnitt 6 das
              Essensfoto (KI_ERNAEHRUNG_FOTO) als einzige Ausnahme. Der Satz
              "darüber hinaus keine Übermittlung an Sprachmodelle" stimmt nur,
              solange KI_FREIGABE aus ist, die KI-Bewertung in den
              Einstellungen aus ist (settings.ai_enabled) UND der WhatsApp-Agent
              nicht auf schatten/nacht/live steht (agent_config.modus). Wird
              eines davon eingeschaltet, MUSS dieser Abschnitt zuerst umgeschrieben
              und der Anbieter ins Dienstleisterverzeichnis aufgenommen werden. */}
          <h2 className="text-xl font-bold text-[#0A0A0A]">5. Erforderlichkeit der Angaben</h2>
          <p>
            Für den Abschluss und die Durchführung des Mitgliedschaftsvertrags benötigen wir
            Name, Anschrift, Geburtsdatum, Kontaktdaten und — bei Lastschrifteinzug — Ihre
            Bankverbindung. Ohne diese Angaben können wir keinen Vertrag schließen. Gesetzlich
            vorgeschrieben ist die Bereitstellung nicht; sie ist jedoch vertraglich erforderlich.
          </p>
          <p>
            Alle weiteren Angaben sind freiwillig. Das gilt insbesondere für gesundheitliche
            Hinweise, Foto- und Videoaufnahmen sowie die Einwilligung in Werbung und
            Reichweitenmessung. Wenn Sie diese Angaben nicht machen oder eine Einwilligung
            widerrufen, entstehen Ihnen keine Nachteile — wir können dann lediglich einzelne
            Zusatzleistungen nicht erbringen, etwa eine auf Vorerkrankungen abgestimmte
            Trainingsgestaltung.
          </p>

          <h2 className="text-xl font-bold text-[#0A0A0A]">6. Keine automatisierte Entscheidungsfindung</h2>
          <p>
            Eine automatisierte Entscheidungsfindung einschließlich Profiling im Sinne des
            Art. 22 DSGVO findet nicht statt. Entscheidungen über Vertragsabschluss, Tarif,
            Graduierung oder Trainingsteilnahme trifft ausschließlich unser Team. An ein System
            künstlicher Intelligenz übermitteln wir Daten nur in einem Fall: wenn Sie im
            Ernährungscoaching ein Essensfoto hochladen und vorher ausdrücklich eingewilligt
            haben (Abschnitt 10.11). Das Ergebnis ist ein Vorschlag, den Sie selbst bestätigen
            oder ändern. Darüber hinaus werden keine Daten an Sprachmodelle oder sonstige
            Systeme künstlicher Intelligenz übermittelt.
          </p>

          <h2 className="text-xl font-bold text-[#0A0A0A]">7. Cookies und lokale Speicherung</h2>
          <p>
            Diese Website verwendet ausschließlich technisch notwendige Cookies und lokale Speicherung
            (Login-Sitzung, Design-Einstellung). Es findet <strong>kein Tracking und keine Analyse durch
            Drittanbieter</strong> statt. Details siehe <a href="/cookies" className="underline">Cookie-Hinweise</a>.
          </p>

          <h2 className="text-xl font-bold text-[#0A0A0A]">8. Ihre Rechte</h2>
          <p>Ihnen stehen folgende Rechte zu:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li><strong>Auskunft</strong> über Ihre gespeicherten Daten (Art. 15 DSGVO)</li>
            <li><strong>Berichtigung</strong> unrichtiger Daten (Art. 16 DSGVO)</li>
            <li><strong>Löschung</strong> (Art. 17 DSGVO)</li>
            <li><strong>Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO)</li>
            <li><strong>Datenübertragbarkeit</strong> (Art. 20 DSGVO)</li>
            <li><strong>Widerspruch</strong> gegen die Verarbeitung, insbesondere gegen Direktwerbung (Art. 21 DSGVO)</li>
            <li><strong>Widerruf erteilter Einwilligungen</strong> jederzeit mit Wirkung für die Zukunft (Art. 7 Abs. 3 DSGVO)</li>
          </ul>
          <p>
            Zur Ausübung Ihrer Rechte genügt eine formlose Nachricht an info@p1-pforzheim.de.
            Sie haben zudem das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren
            (Art. 77 DSGVO) — für Baden-Württemberg: Landesbeauftragter für den Datenschutz und die
            Informationsfreiheit Baden-Württemberg, www.baden-wuerttemberg.datenschutz.de.
          </p>

          <h2 className="text-xl font-bold text-[#0A0A0A]">9. Datensicherheit</h2>
          <p>
            Die Übertragung erfolgt verschlüsselt (TLS/HTTPS). Der Zugriff auf personenbezogene Daten im
            internen System ist rollenbasiert beschränkt; Datenexporte und Löschungen werden protokolliert.
          </p>

          {/* ── App-Abschnitt: Diese Seite ist zugleich die Datenschutzerklärung
              für die Mitglieder-App und wird als Datenschutz-URL in App Store
              und Play Store hinterlegt. Inhalte entsprechen dem tatsächlichen
              Verarbeitungsumfang (Stand 18.08.2026). ── */}
          <h2 className="text-xl font-bold text-[#0A0A0A]">10. Mitglieder-App „P1 OS"</h2>
          <p>
            Dieser Abschnitt gilt zusätzlich für unsere Mitglieder-App. Die App richtet sich
            ausschließlich an Mitglieder des P1 Performance Center; eine Nutzung ohne bestehende
            Mitgliedschaft ist nicht vorgesehen.
          </p>

          <h3 className="text-lg font-semibold text-[#0A0A0A]">10.1 Anmeldung und Sitzung</h3>
          <p>
            Für die Anmeldung verarbeiten wir Ihre E-Mail-Adresse sowie die letzten vier Ziffern Ihrer
            bei uns hinterlegten Mobilfunknummer als Prüfmerkmal. Alternativ können Bestandsmitglieder
            ihren Zugang mit Vor- und Nachname und Mobilfunknummer selbst aktivieren. Nach erfolgreicher
            Anmeldung werden ein kurzlebiges Zugangs-Token und ein widerrufbares Erneuerungs-Token
            erzeugt; letzteres speichern wir ausschließlich als Prüfsumme.
            Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.
          </p>

          <h3 className="text-lg font-semibold text-[#0A0A0A]">10.2 Nutzung der App-Funktionen</h3>
          <ul className="list-disc ml-6 space-y-2">
            <li><strong>Check-in und Anwesenheit</strong> — Zeitpunkt Ihres Studiobesuchs, daraus abgeleitet
              Trainingszähler und Trainingsserie.</li>
            <li><strong>Kursbuchung, Warteliste und feste Wochenplätze</strong> — gebuchte Kurse, Zeitpunkt,
              Position auf der Warteliste, dauerhaft zugewiesene Plätze.</li>
            <li><strong>Trainingsplaner</strong> — von Ihnen erfasste Trainingseinheiten, Übungen, Gewichte,
              Gurtfortschritt. Die Nutzung ist freiwillig.</li>
            <li><strong>Prämien und Empfehlungsprogramm</strong> — Punktestand, eingelöste Prämien sowie
              ein persönlicher Empfehlungscode.</li>
            <li><strong>Shop</strong> — bestellte Artikel und Zuordnung zu Ihrer Mitgliedschaft.</li>
            <li><strong>Akademie</strong> — Freischaltstatus für Videoinhalte.</li>
            <li><strong>Veranstaltungen</strong> — Ihre Zu- oder Absage sowie von Ihnen eingeladene Gäste.</li>
            <li><strong>Support</strong> — Ihre Anfragen an das Studio.</li>
          </ul>
          <p>Rechtsgrundlage jeweils Art. 6 Abs. 1 lit. b DSGVO (Durchführung des Mitgliedsvertrags).</p>

          <h3 className="text-lg font-semibold text-[#0A0A0A]">10.3 Türöffnung</h3>
          <p>
            Die App kann die Studiotür über ein elektronisches Schloss öffnen. Voraussetzung sind eine
            angemeldete Sitzung, eine aktive Mitgliedschaft und ein Check-in innerhalb der vorangegangenen
            zehn Minuten. Jede Öffnung wird mit Ihrer Mitgliedskennung und dem Zeitpunkt protokolliert.
            Das Protokoll dient der Sicherheit des Studiobetriebs und der Nachvollziehbarkeit von Zutritten.
            Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO sowie Art. 6 Abs. 1 lit. f DSGVO (berechtigtes
            Interesse an der Sicherung unserer Räume).
          </p>

          <h3 className="text-lg font-semibold text-[#0A0A0A]">10.4 Familienbereich und Daten von Kindern</h3>
          <p>
            Sorgeberechtigte können über ihr Konto Profile ihrer Kinder verwalten. Dabei verarbeiten wir
            Vor- und Nachname sowie Geburtsdatum des Kindes, Angaben zu einem Notfallkontakt (Name und
            Telefonnummer) und die Namen abholberechtigter Personen. Diese Angaben sind für Trainerinnen
            und Trainer der jeweiligen Kurse einsehbar, damit im Notfall gehandelt und die Abholung
            kontrolliert werden kann.
          </p>
          <p>
            <strong>Gesundheitsdaten werden im Familienbereich bewusst nicht erhoben.</strong> Angaben zu
            Vorerkrankungen oder gesundheitlichen Einschränkungen teilen Sie dem Trainerteam persönlich mit;
            sie werden nicht in der App gespeichert.
          </p>
          <p>
            Kinder haben keinen eigenen Zugang. Ein eigener Login ist erst ab einem im System hinterlegten
            Mindestalter möglich. Die Verknüpfung bestehender Kinderprofile mit einem Elternkonto nimmt
            ausschließlich unser Team vor. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO gegenüber der
            sorgeberechtigten Person.
          </p>

          <h3 className="text-lg font-semibold text-[#0A0A0A]">10.5 Angaben über weitere Personen</h3>
          <p>
            An mehreren Stellen können Sie Daten anderer Personen angeben: Notfallkontakte und
            Abholberechtigte im Familienbereich, Gäste bei Veranstaltungen (Name und Mobilfunknummer)
            sowie Kontakte, die Sie über Ihren Empfehlungslink einladen. Bitte geben Sie solche Daten nur
            an, wenn die betroffene Person damit einverstanden ist. Wir informieren betroffene Personen auf
            Anfrage über die zu ihnen gespeicherten Daten (Art. 14 DSGVO) und löschen sie auf Wunsch.
          </p>

          <h3 className="text-lg font-semibold text-[#0A0A0A]">10.6 Push-Benachrichtigungen</h3>
          <p>
            Wenn Sie Benachrichtigungen zulassen, speichern wir eine Geräte-Kennung (Push-Token) und die
            Plattform Ihres Geräts. Wir versenden Erinnerungen zu gebuchten Kursen, Hinweise zu Prämien
            und Mitteilungen des Studios. Der Versand erfolgt über den Push-Dienst von
            Expo (650 Industries, Inc., Vereinigte Staaten). Übermittelt werden die Geräte-Kennung
            und der Nachrichtentext. Dieser kann Ihren Vornamen und die Bezeichnung eines Kurses
            enthalten — etwa „Training morgen: Bang Muay Thai". Vertrags-, Zahlungs- oder
            Gesundheitsdaten werden nicht übermittelt. Die Zustellung auf Ihr Gerät übernehmen die
            Push-Dienste der Gerätehersteller: auf dem iPhone der Apple Push Notification Service (Apple
            Distribution International Ltd., Irland), auf Android Firebase Cloud Messaging (Google Ireland
            Limited, Irland). Beide erhalten die Geräte-Kennung und den Nachrichtentext; eine Verarbeitung in
            den Vereinigten Staaten ist dabei möglich. Sie können Benachrichtigungen jederzeit in den
            Einstellungen Ihres Geräts abschalten; nicht mehr erreichbare Kennungen löschen wir automatisch.
          </p>

          <h3 className="text-lg font-semibold text-[#0A0A0A]">10.7 Aktualisierungen der App</h3>
          <p>
            Wir können Verbesserungen an der App ausliefern, ohne dass Sie im App Store eine neue
            Fassung laden müssen. Dafür fragt die App <strong>bei jedem Start</strong> beim
            Aktualisierungsdienst von Expo (650 Industries, Inc., Vereinigte Staaten) an, ob eine
            neuere Fassung vorliegt. Übermittelt werden dabei Ihre IP-Adresse, die Art Ihres Geräts
            und die installierte Fassung der App. Ein Bezug zu Ihrem Mitgliedskonto entsteht dabei
            nicht — die Anfrage erfolgt vor der Anmeldung und enthält keine Kontodaten.
          </p>
          <p>
            Die Übermittlung in die Vereinigten Staaten stützen wir auf EU-Standardvertragsklauseln
            (Art. 46 Abs. 2 lit. c DSGVO). Rechtsgrundlage für die Aktualisierung selbst ist unser
            berechtigtes Interesse an einer funktionsfähigen und sicheren App
            (Art. 6 Abs. 1 lit. f DSGVO).
          </p>

          <h3 className="text-lg font-semibold text-[#0A0A0A]">10.8 Berechtigungen der App</h3>
          <p>
            Die App fordert ausschließlich Zugriff auf die <strong>Kamera</strong>. Sie wird für drei
            Zwecke genutzt: zum Scannen des Studio-QR-Codes beim Check-in und — nur wenn Sie das
            selbst auslösen — für Ihr Profilbild und für Essensfotos im Ernährungscoaching
            (Abschnitt 10.11). Das Profilbild bleibt auf Ihrem Gerät gespeichert
            und wird nicht an uns übertragen. Beim QR-Scan werden keine Aufnahmen gespeichert. Ein Zugriff auf Standort, Kontakte, Fotos oder Mikrofon findet nicht statt.
          </p>

          <h3 className="text-lg font-semibold text-[#0A0A0A]">10.9 Was die App nicht tut</h3>
          <ul className="list-disc ml-6 space-y-1">
            <li>keine Erhebung von Standortdaten</li>
            <li>kein Tracking über Apps oder Websites hinweg, keine Werbe-Kennungen</li>
            <li>keine Analyse-, Werbe- oder Absturzberichts-Dienste von Drittanbietern</li>
            <li>keine Weitergabe Ihrer Daten an Dritte zu deren eigenen Zwecken</li>
            <li>kein Verkauf von Daten</li>
          </ul>

          <h3 className="text-lg font-semibold text-[#0A0A0A]">10.10 Löschung</h3>
          <p>
            Auf Wunsch löschen wir Ihre Daten vollständig. Die Löschung erfasst neben den Stammdaten auch
            Check-ins, Trainingseinheiten, Kursbuchungen und feste Plätze, Bestellungen, Prämienpunkte,
            Einwilligungen, Familienverknüpfungen, Veranstaltungsdaten, Support-Anfragen, das Türprotokoll
            sowie die gespeicherten Geräte-Kennungen. Ausgenommen sind Daten, für die gesetzliche
            Aufbewahrungspflichten bestehen. Sie können die Löschung direkt in der App auslösen — im Profil ganz unten unter „Konto löschen". Ihr Zugang endet damit sofort. Alternativ genügt eine formlose Nachricht an info@p1-pforzheim.de.
          </p>

          <h3 className="text-lg font-semibold text-[#0A0A0A]">10.11 Essensfotos im Ernährungscoaching</h3>
          <p>
            Wenn Sie ein Ernährungscoaching gebucht haben, können Sie in der App ein Foto Ihrer Mahlzeit
            hochladen, statt sie von Hand einzutragen. Das ist freiwillig und setzt Ihre ausdrückliche
            Einwilligung voraus, die Sie in der App vor dem ersten Foto erteilen. Weil sich aus dem, was
            Sie essen, Rückschlüsse auf Ihre Gesundheit ergeben können, behandeln wir diese Angaben wie
            Gesundheitsdaten. Rechtsgrundlage: Art. 6 Abs. 1 lit. a und Art. 9 Abs. 2 lit. a DSGVO.
          </p>
          <p>
            Aus dem Foto wird geschätzt, was und wie viel Sie gegessen haben (Gericht, Portion, Kalorien,
            Eiweiß, Kohlenhydrate, Fett). Dafür übermitteln wir das Bild und die Art der Mahlzeit (zum
            Beispiel „Abendessen") an den Dienst Claude der Anthropic PBC, San Francisco, Vereinigte
            Staaten, als Auftragsverarbeiter. <strong>Nicht übermittelt werden Ihr Name, eine Kennung
            Ihres Kontos oder Ihre Ziele.</strong> Aufnahmeort und Gerätedaten entfernen wir vorher aus dem
            Bild. Bitte fotografieren Sie nur die Mahlzeit, keine Personen und keine Schriftstücke.
            Anthropic nutzt die Daten nach den Vertragsbedingungen nicht zum Training seiner Modelle und
            löscht sie in der Regel innerhalb von 30 Tagen. Die Übermittlung in die Vereinigten Staaten
            stützen wir auf EU-Standardvertragsklauseln (Art. 46 Abs. 2 lit. c DSGVO) sowie auf Ihre
            Einwilligung.
          </p>
          <p>
            Die Schätzung ist ein Vorschlag: Erst wenn Sie ihn bestätigen oder ändern, entsteht ein Eintrag
            in Ihrem Ernährungstagebuch. Ihr Coach und die Mitarbeitenden mit Zugriff auf den
            Ernährungsbereich sehen Foto und Eintrag, um Sie zu beraten. Die Fotos speichern wir
            verschlüsselt auf unserem Server in Deutschland. Nicht übernommene Fotos löschen wir nach
            24 Stunden, übernommene, wenn Sie den Eintrag löschen, Ihre Einwilligung widerrufen oder Ihr
            Ernährungscoaching seit 90 Tagen beendet ist. Den Widerruf erklären Sie jederzeit in der App
            oder per E-Mail an info@p1-pforzheim.de; damit werden alle Ihre Essensfotos sofort gelöscht.
            Das Ernährungscoaching ist auch ohne Fotos möglich.
          </p>

          <h2 className="text-xl font-bold text-[#0A0A0A]">11. Kommunikation per E-Mail und WhatsApp</h2>

          <h3 className="text-lg font-semibold text-[#0A0A0A]">11.1 E-Mail</h3>
          <p>
            E-Mails wie Buchungsbestätigungen, Erinnerungen, Vertragsunterlagen und Mitteilungen zu Ihrer
            Mitgliedschaft versenden wir über den Dienst Brevo (Sendinblue SAS, Paris,
            Frankreich) als Auftragsverarbeiter. Übermittelt werden Name, E-Mail-Adresse und
            Inhalt der Nachricht. Wir speichern, ob eine E-Mail zugestellt werden konnte, um den Versand
            wichtiger Mitteilungen nachweisen zu können (Art. 6 Abs. 1 lit. b und f DSGVO). Ob oder wann
            Sie eine E-Mail öffnen oder einen Link anklicken, werten wir nicht aus. Werbliche E-Mails
            erhalten Sie nur mit Ihrer Einwilligung; jede solche E-Mail enthält einen Abmeldelink.
          </p>

          <h3 className="text-lg font-semibold text-[#0A0A0A]">11.2 WhatsApp</h3>
          <p>
            Wenn Sie uns über WhatsApp schreiben oder zugestimmt haben, dass wir Sie dort kontaktieren,
            nutzen wir WhatsApp mit unserer Studionummer. Anbieter ist die WhatsApp Ireland Limited,
            Merrion Road, Dublin 4, Irland. Die Inhalte der Nachrichten sind Ende-zu-Ende-verschlüsselt;
            WhatsApp erhält jedoch Metadaten wie Ihre Telefonnummer, Ihren Profilnamen sowie Zeitpunkt und
            Absender von Nachrichten und verarbeitet diese in eigener Verantwortung, auch in den Vereinigten
            Staaten (Meta Platforms, Inc.). Die Übermittlung stützt sich auf den Angemessenheitsbeschluss
            zum EU-US Data Privacy Framework bzw. auf EU-Standardvertragsklauseln.
          </p>
          <p>
            Ihre Nachrichten speichern wir in unserem Kundenverwaltungssystem, um Ihr Anliegen zu bearbeiten
            (Art. 6 Abs. 1 lit. b DSGVO; werbliche Nachrichten nur mit Einwilligung nach Art. 6 Abs. 1
            lit. a DSGVO). Für Nachrichten von Interessenten gilt die Speicherdauer aus Abschnitt 4 (Prüfung
            und Löschung spätestens 24 Monate nach dem letzten Kontakt), bei Mitgliedern werden sie mit den
            übrigen Mitgliedsdaten gelöscht. Sie müssen WhatsApp nicht nutzen —
            Sie erreichen uns ebenso per Telefon, E-Mail oder direkt im Studio.
          </p>

          <p className="text-sm text-gray-400">Stand: 12. Oktober 2026</p>
        </div>
      </div>
    </div>
  );
}
